<template>
  <v-app id="app-manual">
    <v-container fluid fill-height class="back-ground pa-0">
      <v-layout align-start justify-center>
        <v-flex lg10 md11 sm12 xs12>
          <!-- Header -->
          <v-toolbar color="primary" dark flat class="manual-header">
            <v-btn icon @click="goBack">
              <v-icon>arrow_back</v-icon>
            </v-btn>
            <v-toolbar-title>{{ $t('user_manual') }}</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn icon @click="toggleToc">
              <v-icon>menu</v-icon>
            </v-btn>
          </v-toolbar>

          <!-- Image Lightbox Dialog -->
          <v-dialog v-model="lightboxDialog" max-width="1200" content-class="lightbox-dialog">
            <v-card>
              <v-toolbar dark color="primary" dense>
                <v-toolbar-title>{{ lightboxTitle }}</v-toolbar-title>
                <v-spacer></v-spacer>
                <v-btn icon @click="lightboxDialog = false">
                  <v-icon>close</v-icon>
                </v-btn>
              </v-toolbar>
              <v-card-text class="pa-0 text-xs-center">
                <img :src="lightboxImage" :alt="lightboxTitle" class="lightbox-image" />
              </v-card-text>
            </v-card>
          </v-dialog>

          <v-layout row wrap>
            <!-- Table of Contents - Sidebar -->
            <v-flex xs12 md3 v-show="showToc" class="toc-sidebar">
              <v-card class="toc-card">
                <v-card-title class="primary white--text py-2">
                  <v-icon dark class="mr-2">list</v-icon>
                  {{ $t('table_of_contents') }}
                </v-card-title>
                <v-list dense class="toc-list">
                  <v-list-tile
                    v-for="(section, index) in tableOfContents"
                    :key="index"
                    @click="scrollToSection(section.id)"
                    :class="{ 'active-section': activeSection === section.id }"
                  >
                    <v-list-tile-content>
                      <v-list-tile-title>{{ index + 1 }}. {{ section.title }}</v-list-tile-title>
                    </v-list-tile-content>
                  </v-list-tile>
                </v-list>
              </v-card>
            </v-flex>

            <!-- Main Content -->
            <v-flex xs12 :md9="showToc" :md12="!showToc">
              <v-card class="manual-content">
                <v-card-text class="content-area">
                  <!-- Section 1: Overview -->
                  <section id="section-1" class="manual-section">
                    <h2 class="section-title">
                      <v-icon color="primary" class="mr-2">info</v-icon>
                      1. {{ $t('manual.overview') }}
                    </h2>
                    <v-divider class="mb-3"></v-divider>

                    <h3>1.1 CocoPayroll {{ $t('manual.what_is') }}</h3>
                    <p>{{ $t('manual.overview_desc') }}</p>

                    <h3>1.2 {{ $t('manual.main_features') }}</h3>
                    <v-data-table
                      :headers="featureHeaders"
                      :items="features"
                      hide-actions
                      class="feature-table"
                    >
                      <template slot="items" slot-scope="props">
                        <td><strong>{{ props.item.category }}</strong></td>
                        <td>{{ props.item.description }}</td>
                      </template>
                    </v-data-table>
                  </section>

                  <!-- Section 2: Getting Started -->
                  <section id="section-2" class="manual-section">
                    <h2 class="section-title">
                      <v-icon color="primary" class="mr-2">play_arrow</v-icon>
                      2. {{ $t('manual.getting_started') }}
                    </h2>
                    <v-divider class="mb-3"></v-divider>

                    <h3>2.1 {{ $t('manual.login') }}</h3>
                    <v-stepper vertical class="elevation-0">
                      <v-stepper-step step="1" complete>{{ $t('manual.login_step1') }}</v-stepper-step>
                      <v-stepper-step step="2" complete>{{ $t('manual.login_step2') }}</v-stepper-step>
                      <v-stepper-step step="3" complete>{{ $t('manual.login_step3') }}</v-stepper-step>
                      <v-stepper-step step="4" complete>{{ $t('manual.login_step4') }}</v-stepper-step>
                    </v-stepper>

                    <!-- Screenshot: Login -->
                    <div class="screenshot-container" @click="openLightbox('/static/manual/screenshots/01-login.png', $t('manual.login'))">
                      <v-img
                        :src="'/static/manual/screenshots/01-login.png'"
                        :lazy-src="placeholderImage"
                        aspect-ratio="1.78"
                        class="screenshot-image"
                        contain
                      >
                        <template v-slot:placeholder>
                          <v-layout fill-height align-center justify-center>
                            <div class="screenshot-placeholder">
                              <v-icon large color="grey">image</v-icon>
                              <p class="grey--text">{{ $t('manual.screenshot_login') }}</p>
                            </div>
                          </v-layout>
                        </template>
                      </v-img>
                      <p class="screenshot-caption">{{ $t('manual.fig') }} 1: {{ $t('manual.screenshot_login') }}</p>
                    </div>

                    <h3>2.2 {{ $t('manual.select_company') }}</h3>
                    <p>{{ $t('manual.select_company_desc') }}</p>

                    <!-- Screenshot: Select Company -->
                    <div class="screenshot-container" @click="openLightbox('/static/manual/screenshots/02-select-company.png', $t('manual.select_company'))">
                      <v-img
                        :src="'/static/manual/screenshots/02-select-company.png'"
                        :lazy-src="placeholderImage"
                        aspect-ratio="1.78"
                        class="screenshot-image"
                        contain
                      >
                        <template v-slot:placeholder>
                          <v-layout fill-height align-center justify-center>
                            <div class="screenshot-placeholder">
                              <v-icon large color="grey">image</v-icon>
                              <p class="grey--text">{{ $t('manual.screenshot_select_company') }}</p>
                            </div>
                          </v-layout>
                        </template>
                      </v-img>
                      <p class="screenshot-caption">{{ $t('manual.fig') }} 2: {{ $t('manual.screenshot_select_company') }}</p>
                    </div>

                    <h3>2.3 {{ $t('manual.register') }}</h3>
                    <p>{{ $t('manual.register_desc') }}</p>

                    <h3>2.4 {{ $t('forget_password') }}</h3>
                    <p>{{ $t('manual.forget_password_desc') }}</p>
                  </section>

                  <!-- Section 3: Employee Management -->
                  <section id="section-3" class="manual-section">
                    <h2 class="section-title">
                      <v-icon color="primary" class="mr-2">people</v-icon>
                      3. {{ $t('manual.employee_management') }}
                    </h2>
                    <v-divider class="mb-3"></v-divider>

                    <h3>3.1 {{ $t('manual.employee_list') }}</h3>
                    <p><strong>{{ $t('manual.path') }}:</strong> <code>{{ $t('menu') }} > {{ $t('employee') }} > {{ $t('manual.employee_list') }}</code></p>
                    <ul>
                      <li>{{ $t('manual.employee_list_feature1') }}</li>
                      <li>{{ $t('manual.employee_list_feature2') }}</li>
                      <li>{{ $t('manual.employee_list_feature3') }}</li>
                      <li>{{ $t('manual.employee_list_feature4') }}</li>
                    </ul>

                    <!-- Screenshot: Employee List -->
                    <div class="screenshot-container" @click="openLightbox('/static/manual/screenshots/04-employee-list.png', $t('manual.employee_list'))">
                      <v-img
                        :src="'/static/manual/screenshots/04-employee-list.png'"
                        :lazy-src="placeholderImage"
                        aspect-ratio="1.78"
                        class="screenshot-image"
                        contain
                      >
                        <template v-slot:placeholder>
                          <v-layout fill-height align-center justify-center>
                            <div class="screenshot-placeholder">
                              <v-icon large color="grey">image</v-icon>
                              <p class="grey--text">{{ $t('manual.screenshot_employee_list') }}</p>
                            </div>
                          </v-layout>
                        </template>
                      </v-img>
                      <p class="screenshot-caption">{{ $t('manual.fig') }} 4: {{ $t('manual.screenshot_employee_list') }}</p>
                    </div>

                    <h3>3.2 {{ $t('manual.add_employee') }}</h3>
                    <p><strong>{{ $t('manual.path') }}:</strong> <code>{{ $t('menu') }} > {{ $t('employee') }} > {{ $t('manual.add_employee') }}</code></p>

                    <v-expansion-panel>
                      <v-expansion-panel-content>
                        <div slot="header"><strong>{{ $t('manual.general_info') }}</strong></div>
                        <v-card>
                          <v-card-text>
                            <ul>
                              <li>{{ $t('emp_code') }} *</li>
                              <li>{{ $t('name_title') }}</li>
                              <li>{{ $t('first_name') }} *</li>
                              <li>{{ $t('last_name') }} *</li>
                              <li>{{ $t('nickname') }}</li>
                              <li>{{ $t('manual.gender') }}</li>
                              <li>{{ $t('manual.birthdate') }}</li>
                              <li>{{ $t('id_card') }} *</li>
                            </ul>
                          </v-card-text>
                        </v-card>
                      </v-expansion-panel-content>
                      <v-expansion-panel-content>
                        <div slot="header"><strong>{{ $t('manual.contact_info') }}</strong></div>
                        <v-card>
                          <v-card-text>
                            <ul>
                              <li>{{ $t('address') }}</li>
                              <li>{{ $t('manual.province_district') }}</li>
                              <li>{{ $t('manual.postal_code') }}</li>
                              <li>{{ $t('tel') }}</li>
                              <li>{{ $t('email') }}</li>
                            </ul>
                          </v-card-text>
                        </v-card>
                      </v-expansion-panel-content>
                      <v-expansion-panel-content>
                        <div slot="header"><strong>{{ $t('manual.employment_info') }}</strong></div>
                        <v-card>
                          <v-card-text>
                            <ul>
                              <li>{{ $t('branch') }} *</li>
                              <li>{{ $t('department') }} *</li>
                              <li>{{ $t('position') }} *</li>
                              <li>{{ $t('start_date') }} *</li>
                              <li>{{ $t('manual.employee_type') }}</li>
                            </ul>
                          </v-card-text>
                        </v-card>
                      </v-expansion-panel-content>
                      <v-expansion-panel-content>
                        <div slot="header"><strong>{{ $t('manual.salary_info') }}</strong></div>
                        <v-card>
                          <v-card-text>
                            <ul>
                              <li>{{ $t('salary') }} *</li>
                              <li>{{ $t('manual.payment_type') }}</li>
                              <li>{{ $t('manual.bank_account') }}</li>
                              <li>{{ $t('manual.bank_name') }}</li>
                            </ul>
                          </v-card-text>
                        </v-card>
                      </v-expansion-panel-content>
                    </v-expansion-panel>

                    <!-- Screenshot: Employee Create -->
                    <div class="screenshot-container" @click="openLightbox('/static/manual/screenshots/05-employee-create.png', $t('manual.add_employee'))">
                      <v-img
                        :src="'/static/manual/screenshots/05-employee-create.png'"
                        :lazy-src="placeholderImage"
                        aspect-ratio="1.78"
                        class="screenshot-image"
                        contain
                      >
                        <template v-slot:placeholder>
                          <v-layout fill-height align-center justify-center>
                            <div class="screenshot-placeholder">
                              <v-icon large color="grey">image</v-icon>
                              <p class="grey--text">{{ $t('manual.screenshot_employee_create') }}</p>
                            </div>
                          </v-layout>
                        </template>
                      </v-img>
                      <p class="screenshot-caption">{{ $t('manual.fig') }} 5: {{ $t('manual.screenshot_employee_create') }}</p>
                    </div>

                    <h3>3.3 {{ $t('manual.inactive_employee') }}</h3>
                    <p>{{ $t('manual.inactive_employee_desc') }}</p>
                  </section>

                  <!-- Section 4: Payroll -->
                  <section id="section-4" class="manual-section">
                    <h2 class="section-title">
                      <v-icon color="primary" class="mr-2">attach_money</v-icon>
                      4. {{ $t('manual.payroll_management') }}
                    </h2>
                    <v-divider class="mb-3"></v-divider>

                    <h3>4.1 {{ $t('manual.salary_table') }}</h3>
                    <p><strong>{{ $t('manual.path') }}:</strong> <code>{{ $t('menu') }} > {{ $t('manual.payroll') }} > {{ $t('manual.salary_table') }}</code></p>
                    <p>{{ $t('manual.salary_table_desc') }}</p>

                    <!-- Screenshot: Salary Table -->
                    <div class="screenshot-container" @click="openLightbox('/static/manual/screenshots/06-salary-table.png', $t('manual.salary_table'))">
                      <v-img
                        :src="'/static/manual/screenshots/06-salary-table.png'"
                        :lazy-src="placeholderImage"
                        aspect-ratio="1.78"
                        class="screenshot-image"
                        contain
                      >
                        <template v-slot:placeholder>
                          <v-layout fill-height align-center justify-center>
                            <div class="screenshot-placeholder">
                              <v-icon large color="grey">image</v-icon>
                              <p class="grey--text">{{ $t('manual.screenshot_salary_table') }}</p>
                            </div>
                          </v-layout>
                        </template>
                      </v-img>
                      <p class="screenshot-caption">{{ $t('manual.fig') }} 6: {{ $t('manual.screenshot_salary_table') }}</p>
                    </div>

                    <h3>4.2 {{ $t('manual.payroll_document') }}</h3>
                    <p><strong>{{ $t('manual.path') }}:</strong> <code>{{ $t('menu') }} > {{ $t('manual.payroll') }} > {{ $t('manual.payroll_document') }}</code></p>

                    <v-alert type="info" :value="true" outline class="my-3">
                      {{ $t('manual.payroll_doc_tip') }}
                    </v-alert>

                    <!-- Screenshot: Payroll Doc -->
                    <div class="screenshot-container" @click="openLightbox('/static/manual/screenshots/07-payroll-doc.png', $t('manual.payroll_document'))">
                      <v-img
                        :src="'/static/manual/screenshots/07-payroll-doc.png'"
                        :lazy-src="placeholderImage"
                        aspect-ratio="1.78"
                        class="screenshot-image"
                        contain
                      >
                        <template v-slot:placeholder>
                          <v-layout fill-height align-center justify-center>
                            <div class="screenshot-placeholder">
                              <v-icon large color="grey">image</v-icon>
                              <p class="grey--text">{{ $t('manual.screenshot_payroll_doc') }}</p>
                            </div>
                          </v-layout>
                        </template>
                      </v-img>
                      <p class="screenshot-caption">{{ $t('manual.fig') }} 7: {{ $t('manual.screenshot_payroll_doc') }}</p>
                    </div>

                    <h3>4.3 {{ $t('manual.payslip') }}</h3>
                    <p>{{ $t('manual.payslip_desc') }}</p>
                    <ul>
                      <li>{{ $t('manual.payslip_option1') }}</li>
                      <li>{{ $t('manual.payslip_option2') }}</li>
                      <li>{{ $t('manual.payslip_option3') }}</li>
                    </ul>

                    <!-- Screenshot: Payslip -->
                    <div class="screenshot-container" @click="openLightbox('/static/manual/screenshots/08-payslip.png', $t('manual.payslip'))">
                      <v-img
                        :src="'/static/manual/screenshots/08-payslip.png'"
                        :lazy-src="placeholderImage"
                        aspect-ratio="1.78"
                        class="screenshot-image"
                        contain
                      >
                        <template v-slot:placeholder>
                          <v-layout fill-height align-center justify-center>
                            <div class="screenshot-placeholder">
                              <v-icon large color="grey">image</v-icon>
                              <p class="grey--text">{{ $t('manual.screenshot_payslip') }}</p>
                            </div>
                          </v-layout>
                        </template>
                      </v-img>
                      <p class="screenshot-caption">{{ $t('manual.fig') }} 8: {{ $t('manual.screenshot_payslip') }}</p>
                    </div>
                  </section>

                  <!-- Section 5: Income/Expense -->
                  <section id="section-5" class="manual-section">
                    <h2 class="section-title">
                      <v-icon color="primary" class="mr-2">account_balance_wallet</v-icon>
                      5. {{ $t('manual.income_expense') }}
                    </h2>
                    <v-divider class="mb-3"></v-divider>

                    <h3>5.1 {{ $t('manual.income_types') }}</h3>
                    <v-chip v-for="type in incomeTypes" :key="type" color="green" text-color="white" small class="ma-1">
                      {{ type }}
                    </v-chip>

                    <h3 class="mt-3">5.2 {{ $t('manual.expense_types') }}</h3>
                    <v-chip v-for="type in expenseTypes" :key="type" color="red" text-color="white" small class="ma-1">
                      {{ type }}
                    </v-chip>
                  </section>

                  <!-- Section 6: Time Attendance -->
                  <section id="section-6" class="manual-section">
                    <h2 class="section-title">
                      <v-icon color="primary" class="mr-2">access_time</v-icon>
                      6. {{ $t('manual.time_attendance') }}
                    </h2>
                    <v-divider class="mb-3"></v-divider>

                    <h3>6.1 {{ $t('manual.checkin_checkout') }}</h3>
                    <p>{{ $t('manual.checkin_checkout_desc') }}</p>

                    <h3>6.2 {{ $t('manual.import_fingerprint') }}</h3>
                    <p><strong>{{ $t('manual.path') }}:</strong> <code>{{ $t('menu') }} > {{ $t('manual.import') }} > {{ $t('manual.import_fingerprint') }}</code></p>
                    <p>{{ $t('manual.import_fingerprint_desc') }}</p>
                  </section>

                  <!-- Section 7: Leave Management -->
                  <section id="section-7" class="manual-section">
                    <h2 class="section-title">
                      <v-icon color="primary" class="mr-2">event_busy</v-icon>
                      7. {{ $t('manual.leave_management') }}
                    </h2>
                    <v-divider class="mb-3"></v-divider>

                    <h3>7.1 {{ $t('manual.leave_types') }}</h3>
                    <v-list dense>
                      <v-list-tile v-for="leave in leaveTypes" :key="leave.name">
                        <v-list-tile-avatar>
                          <v-icon :color="leave.color">{{ leave.icon }}</v-icon>
                        </v-list-tile-avatar>
                        <v-list-tile-content>
                          <v-list-tile-title>{{ leave.name }}</v-list-tile-title>
                        </v-list-tile-content>
                      </v-list-tile>
                    </v-list>

                    <!-- Screenshot: Take Leave -->
                    <div class="screenshot-container" @click="openLightbox('/static/manual/screenshots/10-take-leave.png', $t('manual.leave_management'))">
                      <v-img
                        :src="'/static/manual/screenshots/10-take-leave.png'"
                        :lazy-src="placeholderImage"
                        aspect-ratio="1.78"
                        class="screenshot-image"
                        contain
                      >
                        <template v-slot:placeholder>
                          <v-layout fill-height align-center justify-center>
                            <div class="screenshot-placeholder">
                              <v-icon large color="grey">image</v-icon>
                              <p class="grey--text">{{ $t('manual.screenshot_leave') }}</p>
                            </div>
                          </v-layout>
                        </template>
                      </v-img>
                      <p class="screenshot-caption">{{ $t('manual.fig') }} 10: {{ $t('manual.screenshot_leave') }}</p>
                    </div>

                    <h3>7.2 {{ $t('manual.holiday') }}</h3>
                    <p>{{ $t('manual.holiday_desc') }}</p>

                    <!-- Screenshot: Holiday -->
                    <div class="screenshot-container" @click="openLightbox('/static/manual/screenshots/11-holiday.png', $t('manual.holiday'))">
                      <v-img
                        :src="'/static/manual/screenshots/11-holiday.png'"
                        :lazy-src="placeholderImage"
                        aspect-ratio="1.78"
                        class="screenshot-image"
                        contain
                      >
                        <template v-slot:placeholder>
                          <v-layout fill-height align-center justify-center>
                            <div class="screenshot-placeholder">
                              <v-icon large color="grey">image</v-icon>
                              <p class="grey--text">{{ $t('manual.screenshot_holiday') }}</p>
                            </div>
                          </v-layout>
                        </template>
                      </v-img>
                      <p class="screenshot-caption">{{ $t('manual.fig') }} 11: {{ $t('manual.screenshot_holiday') }}</p>
                    </div>
                  </section>

                  <!-- Section 8: Tax & Documents -->
                  <section id="section-8" class="manual-section">
                    <h2 class="section-title">
                      <v-icon color="primary" class="mr-2">description</v-icon>
                      8. {{ $t('manual.tax_documents') }}
                    </h2>
                    <v-divider class="mb-3"></v-divider>

                    <h3>8.1 {{ $t('manual.tax_settings') }}</h3>
                    <p>{{ $t('manual.tax_settings_desc') }}</p>

                    <!-- Screenshot: Tax Settings -->
                    <div class="screenshot-container" @click="openLightbox('/static/manual/screenshots/12-tax-settings.png', $t('manual.tax_settings'))">
                      <v-img
                        :src="'/static/manual/screenshots/12-tax-settings.png'"
                        :lazy-src="placeholderImage"
                        aspect-ratio="1.78"
                        class="screenshot-image"
                        contain
                      >
                        <template v-slot:placeholder>
                          <v-layout fill-height align-center justify-center>
                            <div class="screenshot-placeholder">
                              <v-icon large color="grey">image</v-icon>
                              <p class="grey--text">{{ $t('manual.screenshot_tax') }}</p>
                            </div>
                          </v-layout>
                        </template>
                      </v-img>
                      <p class="screenshot-caption">{{ $t('manual.fig') }} 12: {{ $t('manual.screenshot_tax') }}</p>
                    </div>

                    <h3>8.2 {{ $t('manual.wht_certificate') }}</h3>
                    <p>{{ $t('manual.wht_certificate_desc') }}</p>

                    <h3>8.3 {{ $t('manual.pnd_form') }}</h3>
                    <p>{{ $t('manual.pnd_form_desc') }}</p>
                  </section>

                  <!-- Section 9: Reports -->
                  <section id="section-9" class="manual-section">
                    <h2 class="section-title">
                      <v-icon color="primary" class="mr-2">assessment</v-icon>
                      9. {{ $t('manual.reports') }}
                    </h2>
                    <v-divider class="mb-3"></v-divider>

                    <v-data-table
                      :headers="reportHeaders"
                      :items="reports"
                      hide-actions
                      class="report-table"
                    >
                      <template slot="items" slot-scope="props">
                        <td>{{ props.item.name }}</td>
                        <td>{{ props.item.description }}</td>
                        <td>
                          <v-chip small v-for="format in props.item.formats" :key="format" class="ma-1">{{ format }}</v-chip>
                        </td>
                      </template>
                    </v-data-table>
                  </section>

                  <!-- Section 10: Import Data -->
                  <section id="section-10" class="manual-section">
                    <h2 class="section-title">
                      <v-icon color="primary" class="mr-2">cloud_upload</v-icon>
                      10. {{ $t('manual.import_data') }}
                    </h2>
                    <v-divider class="mb-3"></v-divider>

                    <p>{{ $t('manual.import_data_desc') }}</p>
                    <ul>
                      <li>{{ $t('manual.import_employee') }}</li>
                      <li>{{ $t('manual.import_holiday') }}</li>
                      <li>{{ $t('manual.import_branch') }}</li>
                      <li>{{ $t('manual.import_department') }}</li>
                      <li>{{ $t('manual.import_income') }}</li>
                      <li>{{ $t('manual.import_expense') }}</li>
                      <li>{{ $t('manual.import_time') }}</li>
                    </ul>

                    <v-alert type="warning" :value="true" outline class="my-3">
                      {{ $t('manual.import_warning') }}
                    </v-alert>

                  </section>

                  <!-- Section 11: Settings -->
                  <section id="section-11" class="manual-section">
                    <h2 class="section-title">
                      <v-icon color="primary" class="mr-2">settings</v-icon>
                      11. {{ $t('manual.settings') }}
                    </h2>
                    <v-divider class="mb-3"></v-divider>

                    <h3>11.1 {{ $t('manual.company_settings') }}</h3>
                    <p>{{ $t('manual.company_settings_desc') }}</p>

                    <h3>11.2 {{ $t('manual.branch_settings') }}</h3>
                    <p>{{ $t('manual.branch_settings_desc') }}</p>

                    <h3>11.3 {{ $t('manual.department_settings') }}</h3>
                    <p>{{ $t('manual.department_settings_desc') }}</p>

                    <h3>11.4 {{ $t('manual.general_settings') }}</h3>
                    <p>{{ $t('manual.general_settings_desc') }}</p>

                    <!-- Screenshot: Settings -->
                    <div class="screenshot-container" @click="openLightbox('/static/manual/screenshots/14-settings.png', $t('manual.settings'))">
                      <v-img
                        :src="'/static/manual/screenshots/14-settings.png'"
                        :lazy-src="placeholderImage"
                        aspect-ratio="1.78"
                        class="screenshot-image"
                        contain
                      >
                        <template v-slot:placeholder>
                          <v-layout fill-height align-center justify-center>
                            <div class="screenshot-placeholder">
                              <v-icon large color="grey">image</v-icon>
                              <p class="grey--text">{{ $t('manual.screenshot_settings') }}</p>
                            </div>
                          </v-layout>
                        </template>
                      </v-img>
                      <p class="screenshot-caption">{{ $t('manual.fig') }} 14: {{ $t('manual.screenshot_settings') }}</p>
                    </div>
                  </section>

                  <!-- Section 12: Permissions -->
                  <section id="section-12" class="manual-section">
                    <h2 class="section-title">
                      <v-icon color="primary" class="mr-2">security</v-icon>
                      12. {{ $t('manual.permissions') }}
                    </h2>
                    <v-divider class="mb-3"></v-divider>

                    <h3>12.1 {{ $t('manual.group_permissions') }}</h3>
                    <p>{{ $t('manual.group_permissions_desc') }}</p>

                    <h3>12.2 {{ $t('manual.individual_permissions') }}</h3>
                    <p>{{ $t('manual.individual_permissions_desc') }}</p>

                    <v-data-table
                      :headers="permissionHeaders"
                      :items="permissionLevels"
                      hide-actions
                      class="permission-table"
                    >
                      <template slot="items" slot-scope="props">
                        <td><strong>{{ props.item.level }}</strong></td>
                        <td>{{ props.item.access }}</td>
                      </template>
                    </v-data-table>
                  </section>

                  <!-- Section 13: Employee Portal -->
                  <section id="section-13" class="manual-section">
                    <h2 class="section-title">
                      <v-icon color="primary" class="mr-2">person</v-icon>
                      13. {{ $t('manual.employee_portal') }}
                    </h2>
                    <v-divider class="mb-3"></v-divider>

                    <h3>13.1 {{ $t('manual.employee_dashboard') }}</h3>
                    <p>{{ $t('manual.employee_dashboard_desc') }}</p>

                    <h3>13.2 {{ $t('manual.check_salary') }}</h3>
                    <p>{{ $t('manual.check_salary_desc') }}</p>

                    <h3>13.3 {{ $t('manual.employee_profile') }}</h3>
                    <p>{{ $t('manual.employee_profile_desc') }}</p>
                  </section>

                  <!-- Section 14: Troubleshooting -->
                  <section id="section-14" class="manual-section">
                    <h2 class="section-title">
                      <v-icon color="primary" class="mr-2">help</v-icon>
                      14. {{ $t('manual.troubleshooting') }}
                    </h2>
                    <v-divider class="mb-3"></v-divider>

                    <v-expansion-panel>
                      <v-expansion-panel-content v-for="(issue, index) in troubleshooting" :key="index">
                        <div slot="header"><strong>{{ issue.problem }}</strong></div>
                        <v-card>
                          <v-card-text>
                            <ul>
                              <li v-for="(solution, sIndex) in issue.solutions" :key="sIndex">{{ solution }}</li>
                            </ul>
                          </v-card-text>
                        </v-card>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </section>

                  <!-- Section 15: Contact -->
                  <section id="section-15" class="manual-section">
                    <h2 class="section-title">
                      <v-icon color="primary" class="mr-2">contact_support</v-icon>
                      15. {{ $t('manual.contact') }}
                    </h2>
                    <v-divider class="mb-3"></v-divider>

                    <v-card class="contact-card">
                      <v-card-text>
                        <v-layout row wrap>
                          <v-flex xs12 md4 class="text-xs-center pa-3">
                            <v-icon large color="primary">email</v-icon>
                            <p class="mt-2"><strong>{{ $t('email') }}</strong></p>
                            <p>support@cocopayroll.com</p>
                          </v-flex>
                          <v-flex xs12 md4 class="text-xs-center pa-3">
                            <v-icon large color="primary">phone</v-icon>
                            <p class="mt-2"><strong>{{ $t('tel') }}</strong></p>
                            <p>02-XXX-XXXX</p>
                          </v-flex>
                          <v-flex xs12 md4 class="text-xs-center pa-3">
                            <v-icon large color="primary">chat</v-icon>
                            <p class="mt-2"><strong>Line</strong></p>
                            <p>@cocopayroll</p>
                          </v-flex>
                        </v-layout>
                      </v-card-text>
                    </v-card>
                  </section>

                </v-card-text>
              </v-card>
            </v-flex>
          </v-layout>

          <!-- Footer -->
          <v-card class="mt-3 text-xs-center pa-3" color="grey lighten-4">
            <p class="mb-0">
              <strong>CocoPayroll</strong> Version {{ version }} |
              {{ $t('manual.last_updated') }}: {{ $t('manual.update_date') }}
            </p>
          </v-card>
        </v-flex>
      </v-layout>
    </v-container>
  </v-app>
</template>

<script>
import { mapState } from "vuex";

export default {
  name: "UserManual",
  data() {
    return {
      showToc: true,
      activeSection: "section-1",
      lightboxDialog: false,
      lightboxImage: "",
      lightboxTitle: "",
      placeholderImage: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='225' viewBox='0 0 400 225'%3E%3Crect fill='%23f5f5f5' width='400' height='225'/%3E%3Ctext fill='%239e9e9e' font-family='sans-serif' font-size='14' x='50%25' y='50%25' text-anchor='middle' dy='.3em'%3EScreenshot%3C/text%3E%3C/svg%3E",
      featureHeaders: [
        { text: this.$t('manual.category'), value: 'category', sortable: false },
        { text: this.$t('manual.description'), value: 'description', sortable: false }
      ],
      reportHeaders: [
        { text: this.$t('manual.report_name'), value: 'name', sortable: false },
        { text: this.$t('manual.description'), value: 'description', sortable: false },
        { text: this.$t('manual.format'), value: 'formats', sortable: false }
      ],
      permissionHeaders: [
        { text: this.$t('manual.level'), value: 'level', sortable: false },
        { text: this.$t('manual.access'), value: 'access', sortable: false }
      ]
    };
  },
  computed: {
    ...mapState("app", ["version"]),
    tableOfContents() {
      return [
        { id: 'section-1', title: this.$t('manual.overview') },
        { id: 'section-2', title: this.$t('manual.getting_started') },
        { id: 'section-3', title: this.$t('manual.employee_management') },
        { id: 'section-4', title: this.$t('manual.payroll_management') },
        { id: 'section-5', title: this.$t('manual.income_expense') },
        { id: 'section-6', title: this.$t('manual.time_attendance') },
        { id: 'section-7', title: this.$t('manual.leave_management') },
        { id: 'section-8', title: this.$t('manual.tax_documents') },
        { id: 'section-9', title: this.$t('manual.reports') },
        { id: 'section-10', title: this.$t('manual.import_data') },
        { id: 'section-11', title: this.$t('manual.settings') },
        { id: 'section-12', title: this.$t('manual.permissions') },
        { id: 'section-13', title: this.$t('manual.employee_portal') },
        { id: 'section-14', title: this.$t('manual.troubleshooting') },
        { id: 'section-15', title: this.$t('manual.contact') }
      ];
    },
    features() {
      return [
        { category: this.$t('manual.feature_employee'), description: this.$t('manual.feature_employee_desc') },
        { category: this.$t('manual.feature_payroll'), description: this.$t('manual.feature_payroll_desc') },
        { category: this.$t('manual.feature_time'), description: this.$t('manual.feature_time_desc') },
        { category: this.$t('manual.feature_leave'), description: this.$t('manual.feature_leave_desc') },
        { category: this.$t('manual.feature_tax'), description: this.$t('manual.feature_tax_desc') },
        { category: this.$t('manual.feature_report'), description: this.$t('manual.feature_report_desc') }
      ];
    },
    incomeTypes() {
      return [
        this.$t('salary'),
        this.$t('manual.ot'),
        this.$t('manual.bonus'),
        this.$t('manual.commission'),
        this.$t('manual.allowance'),
        this.$t('manual.position_allowance')
      ];
    },
    expenseTypes() {
      return [
        this.$t('manual.social_security'),
        this.$t('manual.withholding_tax'),
        this.$t('manual.loan'),
        this.$t('manual.insurance'),
        this.$t('manual.accommodation')
      ];
    },
    leaveTypes() {
      return [
        { name: this.$t('manual.annual_leave'), icon: 'beach_access', color: 'blue' },
        { name: this.$t('manual.sick_leave'), icon: 'local_hospital', color: 'red' },
        { name: this.$t('manual.personal_leave'), icon: 'person', color: 'orange' },
        { name: this.$t('manual.maternity_leave'), icon: 'child_care', color: 'pink' },
        { name: this.$t('manual.ordination_leave'), icon: 'account_balance', color: 'purple' },
        { name: this.$t('manual.leave_without_pay'), icon: 'money_off', color: 'grey' }
      ];
    },
    reports() {
      return [
        { name: this.$t('manual.salary_report'), description: this.$t('manual.salary_report_desc'), formats: ['PDF', 'Excel'] },
        { name: this.$t('manual.payslip'), description: this.$t('manual.payslip_report_desc'), formats: ['PDF'] },
        { name: this.$t('manual.wht_cert_50'), description: this.$t('manual.wht_cert_desc'), formats: ['PDF'] },
        { name: this.$t('manual.pnd1'), description: this.$t('manual.pnd1_desc'), formats: ['PDF'] },
        { name: this.$t('manual.contribution_report'), description: this.$t('manual.contribution_report_desc'), formats: ['PDF', 'Excel'] }
      ];
    },
    permissionLevels() {
      return [
        { level: 'Super Admin', access: this.$t('manual.super_admin_access') },
        { level: 'Admin', access: this.$t('manual.admin_access') },
        { level: 'HR', access: this.$t('manual.hr_access') },
        { level: 'Manager', access: this.$t('manual.manager_access') },
        { level: 'Employee', access: this.$t('manual.employee_access') }
      ];
    },
    troubleshooting() {
      return [
        {
          problem: this.$t('manual.problem_login'),
          solutions: [
            this.$t('manual.solution_login1'),
            this.$t('manual.solution_login2'),
            this.$t('manual.solution_login3'),
            this.$t('manual.solution_login4')
          ]
        },
        {
          problem: this.$t('manual.problem_payroll'),
          solutions: [
            this.$t('manual.solution_payroll1'),
            this.$t('manual.solution_payroll2'),
            this.$t('manual.solution_payroll3')
          ]
        },
        {
          problem: this.$t('manual.problem_import'),
          solutions: [
            this.$t('manual.solution_import1'),
            this.$t('manual.solution_import2'),
            this.$t('manual.solution_import3')
          ]
        },
        {
          problem: this.$t('manual.problem_pdf'),
          solutions: [
            this.$t('manual.solution_pdf1'),
            this.$t('manual.solution_pdf2')
          ]
        }
      ];
    }
  },
  methods: {
    goBack() {
      this.$router.push('/login');
    },
    toggleToc() {
      this.showToc = !this.showToc;
    },
    scrollToSection(sectionId) {
      this.activeSection = sectionId;
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    },
    openLightbox(imageSrc, title) {
      this.lightboxImage = imageSrc;
      this.lightboxTitle = title;
      this.lightboxDialog = true;
    }
  },
  mounted() {
    // Track scroll position to update active section
    const contentArea = document.querySelector('.content-area');
    if (contentArea) {
      contentArea.addEventListener('scroll', () => {
        const sections = document.querySelectorAll('.manual-section');
        sections.forEach(section => {
          const rect = section.getBoundingClientRect();
          if (rect.top >= 0 && rect.top <= 200) {
            this.activeSection = section.id;
          }
        });
      });
    }
  }
};
</script>

<style scoped>
.back-ground {
  background: linear-gradient(to bottom right, #e3f2fd, #f5f5f5) !important;
  min-height: 100vh;
}

.manual-header {
  border-radius: 8px 8px 0 0;
}

.toc-sidebar {
  position: sticky;
  top: 0;
  max-height: calc(100vh - 100px);
  overflow-y: auto;
}

.toc-card {
  border-radius: 0 0 0 8px;
}

.toc-list {
  max-height: calc(100vh - 200px);
  overflow-y: auto;
}

.active-section {
  background-color: #e3f2fd !important;
  border-left: 3px solid #1976d2;
}

.manual-content {
  border-radius: 0 0 8px 0;
  min-height: calc(100vh - 200px);
}

.content-area {
  max-height: calc(100vh - 150px);
  overflow-y: auto;
  padding: 24px;
}

.manual-section {
  margin-bottom: 40px;
  padding-top: 20px;
}

.section-title {
  color: #1976d2;
  font-size: 1.5em;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
}

h3 {
  color: #424242;
  margin-top: 20px;
  margin-bottom: 10px;
}

p {
  line-height: 1.8;
  color: #616161;
}

ul {
  margin-left: 20px;
  line-height: 2;
}

code {
  background-color: #f5f5f5;
  padding: 2px 8px;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
}

.feature-table, .report-table, .permission-table {
  margin-top: 16px;
  margin-bottom: 16px;
}

.contact-card {
  background: linear-gradient(to right, #e3f2fd, #fff) !important;
}

/* Screenshot styles */
.screenshot-container {
  margin: 20px 0;
  cursor: pointer;
  transition: transform 0.2s;
}

.screenshot-container:hover {
  transform: scale(1.02);
}

.screenshot-image {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  max-width: 100%;
  background-color: #fafafa;
}

.screenshot-placeholder {
  text-align: center;
  padding: 40px;
  background-color: #f5f5f5;
  border-radius: 8px;
}

.screenshot-placeholder p {
  margin-top: 10px;
  font-size: 12px;
}

.screenshot-caption {
  text-align: center;
  font-size: 0.9em;
  color: #757575;
  margin-top: 8px;
  font-style: italic;
}

/* Lightbox styles */
.lightbox-image {
  max-width: 100%;
  max-height: 80vh;
  display: block;
  margin: 0 auto;
}

@media (max-width: 960px) {
  .toc-sidebar {
    position: relative;
    max-height: none;
  }

  .content-area {
    max-height: none;
  }

  .screenshot-container:hover {
    transform: none;
  }
}
</style>
